import type { CSSProperties } from "react";
import Skeleton from "react-loading-skeleton";
import { css } from "../../../../styled-system/css";
import { Flex, styled, VStack } from "../../../../styled-system/jsx";
import { HoverInfo } from "../basic/Tooltip";
import TotalSalesVendorWarning from "./TotalSalesVendorWarning";
import { formatMetricValue, type FormatOptions } from "utils/formatNumber";
import { useLocalStorage } from "usehooks-ts";
import { useAppSelector } from "redux/hooks";

interface Props {
  name: string;
  value?: number | null;
  target?: number | null;
  showTarget?: boolean;
  lower?: boolean;
  targetLabel?: string | null;
  targetColor?: string | null;
  legendColor?: string;
  selected?: boolean;
  toolTip?: string;
  loading?: boolean;
  formatOptions: FormatOptions;
  compareFormatOptions?: FormatOptions;
}

const SelectedIndicator = styled("div", {
  base: {
    borderColor: "var(--color)",
    borderRadius: "light",
    width: "6px",
    backgroundColor: "var(--color)",
    transition: "all 0.30s ease-in-out",
  },
  variants: {
    isSelected: {
      true: { boxShadow: `0 0 14px var(--color)` },
      false: { backgroundColor: `color-mix(in srgb, var(--color) 20%, transparent)` },
    },
  },
});

const SummaryMetric = ({
  name,
  value,
  target,
  showTarget = false,
  lower = false,
  targetLabel = "vs. target",
  targetColor,
  legendColor,
  selected,
  toolTip,
  loading = false,
  formatOptions = {
    format: "number",
    default: "-",
    rounded: "down",
    compact: true,
    addPlus: false,
    decimalPlaces: 1,
  },
  compareFormatOptions = {
    format: "number",
    default: "-",
    rounded: "down",
    compact: true,
    addPlus: true,
    decimalPlaces: 1,
  },
}: Props) => {
  const [numberFormat] = useLocalStorage("numberFormat", "compact");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const comparator = (a: React.ReactNode, b: React.ReactNode) => {
    if (typeof a !== "number" || typeof b !== "number") {
      return false;
    }
    return lower ? a <= b : a >= b;
  };

  let color = comparator(value, target) ? "#007000" : "#d2222d";
  if (targetColor) {
    color = targetColor;
  }

  const useCompact = formatOptions.format === "percent" ? false : (formatOptions.compact ?? numberFormat !== "precise");

  const adjustedFormatOptions: FormatOptions =
    formatOptions.format === "percent" ? formatOptions : { ...formatOptions, compact: useCompact };
  const adjustedCompareOptions: FormatOptions =
    compareFormatOptions.format === "percent" ?
      { ...compareFormatOptions, decimalPlaces: compareFormatOptions.decimalPlaces ?? 1 }
    : { ...compareFormatOptions, compact: compareFormatOptions.compact ?? numberFormat !== "precise" };

  const displayValue = formatMetricValue(
    value,
    adjustedFormatOptions,
    formatOptions.overrideMarketplace ?? marketPlace,
  );
  const displayCompareValue = formatMetricValue(
    target,
    adjustedCompareOptions,
    formatOptions.overrideMarketplace ?? marketPlace,
  );

  return (
    <Flex gap="medium" justifyContent="flex-end">
      <VStack gap="small" justifyContent="space-between" alignItems={"end"}>
        <h6
          className={css({
            display: "flex",
            gap: "xsmall",
            justifyContent: "end",
            alignItems: "start",
            height: "34px",
            margin: 0,
            fontSize: "medium",
            fontWeight: selected ? 800 : 600,
            lineHeight: "1",
            textAlign: "end",
            transition: "all 0.30s ease-in-out",
          })}
        >
          <TotalSalesVendorWarning metricName={name} />
          {name}
          {toolTip && <HoverInfo iconClassName={css({ color: "grey", fontSize: "small" })}>{toolTip}</HoverInfo>}
        </h6>
        <VStack gap="xsmall" alignItems={"end"}>
          <h3
            className={css({
              margin: 0,
              fontSize: "xlarge",
              fontWeight: 600,
              lineHeight: "1",
            })}
          >
            {loading ?
              <Skeleton width={"50px"} />
            : displayValue}
          </h3>
          <small className={css({ minHeight: "20px" })}>
            {showTarget ?
              <>
                {targetLabel} <span className={css({ color, fontWeight: "bold" })}>{displayCompareValue}</span>
              </>
            : <>&nbsp;</>}
          </small>
        </VStack>
      </VStack>
      {legendColor ?
        <SelectedIndicator
          isSelected={selected}
          style={
            {
              "--color": legendColor,
            } as CSSProperties
          }
        />
      : null}
    </Flex>
  );
};

export default SummaryMetric;
